import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Check, Tag } from "lucide-react";

function fmtMxn(n) { return "$" + Number(n || 0).toLocaleString("es-MX"); }

/**
 * Fase 13.4 · Order bump card en checkout.
 * Fetches the current match, muestra checkbox grande y llama apply/skip.
 * Delega refresh del cart al parent vía `onChanged`.
 *
 * Palette: usa tokens semánticos (bg-primary / text-primary / border-primary)
 * para respetar el rebrand naranja+negro+blanco que viene en Fase 13.4.5.
 */
export default function OrderBumpCard({ onChanged }) {
  const [bump, setBump] = useState(null);
  const [isSelected, setIsSelected] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/checkout/bumps")
      .then((r) => {
        setBump(r.data.bump);
        setIsSelected(!!r.data.is_selected);
      })
      .catch(() => setBump(null));
  }, []);

  if (!bump) return null;

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isSelected) {
        await api.post(`/checkout/bumps/${bump.id}/skip`);
        setIsSelected(false);
        toast.success("Complemento removido");
      } else {
        await api.post(`/checkout/bumps/${bump.id}/apply`);
        setIsSelected(true);
        toast.success("¡Complemento añadido!");
      }
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const discountPct = bump.original_price_mxn > 0
    ? Math.round((1 - bump.price_mxn / bump.original_price_mxn) * 100)
    : 0;

  return (
    <div
      data-testid="order-bump-card"
      onClick={toggle}
      className={`mt-6 p-5 rounded-xl border-2 transition-all cursor-pointer ${
        isSelected
          ? "border-primary bg-primary/10"
          : "border-dashed border-primary bg-primary/5 hover:bg-primary/10"
      }`}
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest">
          <Zap className="w-3 h-3" /> Oferta exclusiva
        </span>
        {discountPct > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-foreground text-background text-[10px] font-bold uppercase tracking-widest">
            <Tag className="w-3 h-3" /> {discountPct}% OFF
          </span>
        )}
      </div>
      {bump.headline && (
        <p className="text-xs font-bold uppercase tracking-widest text-primary mb-1">
          {bump.headline}
        </p>
      )}
      <div className="flex items-start gap-3">
        <label className="flex items-start gap-3 cursor-pointer flex-1" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={toggle}
            disabled={busy}
            data-testid="order-bump-toggle"
            className="mt-1 w-5 h-5 rounded border-2 border-primary accent-primary cursor-pointer flex-shrink-0"
          />
          <div className="flex-1">
            <p className="text-base font-bold text-foreground">
              Sí, añade{" "}
              <span className="underline decoration-2 underline-offset-4 decoration-primary">
                {bump.title}
              </span>
            </p>
            {bump.description && (
              <p className="mt-1 text-sm text-muted-foreground">{bump.description}</p>
            )}
            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-extrabold text-foreground tabular-nums" data-testid="order-bump-price">
                {fmtMxn(bump.price_mxn)}
              </span>
              {bump.original_price_mxn > bump.price_mxn && (
                <span className="text-sm text-muted-foreground line-through font-mono">
                  {fmtMxn(bump.original_price_mxn)}
                </span>
              )}
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">MXN</span>
            </div>
          </div>
        </label>
        {isSelected && (
          <div className="flex-shrink-0 flex items-center gap-1 text-primary text-sm font-bold">
            <Check className="w-4 h-4" /> Añadido
          </div>
        )}
      </div>
    </div>
  );
}
